import Chart from "chart.js/auto";
import "chartjs-adapter-date-fns";
import { LineController } from "chart.js";

let chart = null;

export const ChartHook = {
  mounted() {
    renderChart(this.el, this);
  },
  updated() {
    updateChart(this.el, this);
  },
};

function renderChart(chartEl, hook) {
  const ctx = chartEl.getContext("2d");
  const primaryColor = getComputedStyle(chartEl).getPropertyValue("--color-primary");
  const options = buildOptions(chartEl.dataset.options, primaryColor);
  const gradients = buildGradients(ctx, chartEl.dataset.gradients);
  const data = buildData(chartEl.dataset.data, gradients);

  chart = new Chart(ctx, {
    type: chartEl.dataset.type,
    data: data,
    options: options,
  });

  const clickEvent = chartEl.dataset.clickEvent;
  if (clickEvent) {
    chartEl.addEventListener("click", (evt) => {
      const elements = chart.getElementsAtEventForMode(evt, "nearest", { intersect: true }, true);
      if (elements.length) {
        const clickTarget = chartEl.dataset.clickTarget;
        if (clickTarget) {
          hook.pushEventTo(`#${clickTarget}`, clickEvent, {
            index: elements[0].index,
          });
        } else {
          hook.pushEvent(clickEvent, { index: elements[0].index });
        }
      }
    });
  }

  setTimeout(() => {
    chartEl.setAttribute("ready", true);
  }, 4000);
}

function updateChart(chartEl) {
  const ctx = chartEl.getContext("2d");
  const gradients = buildGradients(ctx, chartEl.dataset.gradients);

  chart.data = buildData(chartEl.dataset.data, gradients);
  chart.update();
  chart.resize();
}

function buildOptions(json, _primaryColor) {
  let options = JSON.parse(json || "{}");
  if (options.scales) {
    options.scales = _.mapValues(options.scales, (axis) => {
      axis.ticks ||= {};
      if (axis.ticks.formatNumber) {
        axis.ticks.callback = (value) => formatNumber(value);
      }
      if (axis.ticks.percentage) {
        axis.ticks.callback = (value) => `${value}%`;
      }
      return axis;
    });
  }

  const formatNumber = (n) => {
    if (n < 1e3) return n.toString();
    if (n >= 1e3 && n < 1e6) return `${+(n / 1e3).toFixed(1)}K`;
    if (n >= 1e6 && n < 1e9) return `${+(n / 1e6).toFixed(1)}M`;
    if (n >= 1e9 && n < 1e12) return `${+(n / 1e9).toFixed(1)}B`;
    if (n >= 1e12) return `${+(n / 1e12).toFixed(1)}B`;
  };

  const defaultOptions = {
    plugins: {
      tooltip: {
        enabled: true,
        intersect: false,
        yAlign: "bottom",
        caretPadding: 10,
        caretSize: 0,
        displayColors: false,
        backgroundColor: (tooltipItem) => {
          if (options.plugins?.tooltip?.useDataSetColor) {
            return tooltipItem.tooltip.labelColors[0].backgroundColor;
          }
          return "black";
        },
        callbacks: {
          label: (tooltip) => formatNumber(tooltip.raw),
          title: () => "",
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };
  options = Object.assign(defaultOptions, options);
  return options;
}

function buildGradients(ctx, json) {
  const gradients = JSON.parse(json || "{}");
  return Object.entries(gradients).reduce((acc, [key, value]) => {
    const {
      colorStops,
      bounds: [x0, y0, x1, y1],
    } = value;
    const gradient = ctx.createLinearGradient(x0, y0, x1, y1);

    for (const colorStop of colorStops) {
      const [offset, color] = colorStop;
      gradient.addColorStop(offset, color);
    }

    acc[key] = gradient;
    return acc;
  }, {});
}

function buildData(json, gradients) {
  const data = JSON.parse(json || "{}");
  data.datasets = data.datasets.map((dataset) => {
    const backgroundColorGradient = gradients[dataset.backgroundColorGradient];
    if (backgroundColorGradient) {
      dataset.backgroundColor = backgroundColorGradient;
    }
    const borderGradient = gradients[dataset.borderGradient];
    if (borderGradient) {
      dataset.borderColor = borderGradient;
    }
    return dataset;
  });
  return data;
}

class ShadowLineController extends LineController {
  draw(...args) {
    super.draw(...args);
    const ctx = this.chart.ctx;
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.15)";
    ctx.shadowBlur = 10;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 10;
    ctx.responsive = true;
    ctx.stroke();
    super.draw(...args);
    ctx.restore();
  }
}

ShadowLineController.id = "shadow-line";
ShadowLineController.defaults = LineController.defaults;
Chart.register(ShadowLineController);
